import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload, Music, CheckCircle, AlertCircle, Loader2, X,
  Zap, Clock, BarChart2, HardDrive, ChevronRight, RefreshCw, Library,
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/App';
import { useAuth } from '@/context/AuthContext';

// ─── Credit cost helper (mirrors backend logic) ────────────────────────────
function uploadCreditCost(bytes) {
  const mb = bytes / (1024 * 1024);
  if (mb < 5)  return 1;
  if (mb < 15) return 2;
  if (mb < 30) return 3;
  return 4;
}

function fmtSize(bytes) {
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;
}

const STATUS_META = {
  pending:   { label: 'Waiting',          color: 'text-amber-500',   icon: Clock },
  uploading: { label: 'Uploading',        color: 'text-blue-500',    icon: Loader2 },
  uploaded:  { label: 'Uploaded',         color: 'text-emerald-500', icon: CheckCircle },
  raw:       { label: 'Stored',           color: 'text-[#64748B]',   icon: HardDrive },
  queued:    { label: 'Queued',           color: 'text-amber-500',   icon: Clock },
  analyzing: { label: 'Analyzing',        color: 'text-blue-500',    icon: Loader2 },
  ready:     { label: 'Analyzed',         color: 'text-emerald-500', icon: CheckCircle },
  failed:    { label: 'Failed',           color: 'text-red-500',     icon: AlertCircle },
  duplicate: { label: 'Already in library', color: 'text-violet-500', icon: Library },
};

export default function UploadPage() {
  const { user, setUser, isSubscribed } = useAuth();
  const [files, setFiles] = useState([]);   // [{file, id, status, trackId, progress, error, analysis, isDuplicate, existingTrackId}]
  const [isDragging, setIsDragging] = useState(false);
  const [credits, setCredits] = useState(user?.credits ?? 0);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const pollTimers = useRef({});
  // Map of filename → track_id for the user's existing library (used for duplicate detection)
  const libraryRef = useRef({});

  // Load library filenames once for duplicate pre-check
  useEffect(() => {
    axios.get(`${API}/tracks`).then(r => {
      const all = Array.isArray(r.data) ? r.data : (r.data?.tracks || []);
      const map = {};
      all.forEach(t => {
        if (t.original_filename) map[t.original_filename.toLowerCase()] = t.id;
        // Also index by "Artist - Title.mp3" pattern for renamed files
        if (t.title && t.artist) map[`${t.artist} - ${t.title}`.toLowerCase()] = t.id;
      });
      libraryRef.current = map;
    }).catch(() => {});
  }, []);

  // Fetch current credit balance
  useEffect(() => {
    axios.get(`${API}/credits/balance`).then(r => setCredits(r.data.credits)).catch(() => {});
  }, []);

  // Cleanup polling timers on unmount
  useEffect(() => {
    return () => Object.values(pollTimers.current).forEach(clearInterval);
  }, []);

  const totalUploadCost = files.reduce((s, f) => s + (f.status === 'pending' ? uploadCreditCost(f.file.size) : 0), 0);
  const canUpload = files.some(f => f.status === 'pending') && credits >= totalUploadCost && !uploading;

  // ─── File selection ───────────────────────────────────────────────────────
  const addFiles = useCallback((incoming) => {
    const mp3s = Array.from(incoming).filter(f => f.type === 'audio/mpeg' || f.name.toLowerCase().endsWith('.mp3'));
    if (!mp3s.length) { toast.error('Only MP3 files are supported'); return; }
    setFiles(prev => {
      const existing = new Set(prev.map(f => f.file.name + f.file.size));
      const novel = mp3s.filter(f => !existing.has(f.name + f.size));
      if (!novel.length) { toast('All selected files are already in the queue'); return prev; }
      const newEntries = novel.map(f => {
        const key = f.name.toLowerCase();
        const nameWithoutExt = f.name.replace(/\.mp3$/i, '').toLowerCase();
        const existingId = libraryRef.current[key] || libraryRef.current[nameWithoutExt] || null;
        return {
          id: crypto.randomUUID(),
          file: f,
          status: existingId ? 'duplicate' : 'pending',
          progress: 0,
          trackId: existingId || null,
          error: null,
          analysis: null,
          isDuplicate: !!existingId,
          existingTrackId: existingId,
        };
      });
      const dupeCount = newEntries.filter(e => e.isDuplicate).length;
      if (dupeCount) toast.warning(`${dupeCount} file${dupeCount > 1 ? 's' : ''} already in your library — shown below`);
      return [...prev, ...newEntries];
    });
  }, []);

  const handleDrop = (e) => {
    e.preventDefault(); setIsDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const removeFile = (id) => {
    clearInterval(pollTimers.current[id]);
    setFiles(prev => prev.filter(f => f.id !== id));
  };

  const updateFile = (id, patch) => setFiles(prev => prev.map(f => f.id === id ? { ...f, ...patch } : f));

  // ─── Upload all pending files ─────────────────────────────────────────────
  // 3-step direct-to-R2 flow (bypasses Cloudflare/nginx 100MB body cap):
  //   1) POST /tracks/upload/init    → presigned PUT URL + track_id
  //   2) PUT   <upload_url>          → axios streams file straight to R2
  //   3) POST /tracks/upload/complete/{id} → server verifies + finalises
  const uploadAll = async () => {
    setUploading(true);
    const pending = files.filter(f => f.status === 'pending');

    for (const item of pending) {
      updateFile(item.id, { status: 'uploading', progress: 0 });
      let initData = null;
      try {
        // ── 1) INIT ────────────────────────────────────────────────────
        const initRes = await axios.post(`${API}/tracks/upload/init`, {
          filename: item.file.name,
          file_size: item.file.size,
          content_type: item.file.type || 'audio/mpeg',
        });
        initData = initRes.data;

        // Backend detected a duplicate — treat as duplicate, not failure.
        if (initData?.duplicate) {
          updateFile(item.id, {
            status: 'duplicate',
            isDuplicate: true,
            existingTrackId: initData.existing_track_id,
            trackId: initData.existing_track_id,
            error: null,
          });
          toast(`Already in library: ${item.file.name}`);
          continue;
        }

        // ── 2) DIRECT PUT TO R2 ────────────────────────────────────────
        // Use a raw XMLHttpRequest so we (a) get real upload progress and
        // (b) don't leak our global Authorization header to Cloudflare —
        // that would invalidate the presigned signature.
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('PUT', initData.upload_url, true);
          const headers = initData.headers || { 'Content-Type': initData.content_type };
          Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
          xhr.upload.onprogress = (e) => {
            if (!e.lengthComputable) return;
            const pct = Math.round((e.loaded / e.total) * 100);
            updateFile(item.id, { progress: pct });
          };
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve();
            else reject(new Error(`R2 upload failed (${xhr.status})`));
          };
          xhr.onerror = () => reject(new Error('Network error uploading to storage'));
          xhr.onabort = () => reject(new Error('Upload aborted'));
          xhr.send(item.file);
        });

        // ── 3) COMPLETE ────────────────────────────────────────────────
        const completeRes = await axios.post(
          `${API}/tracks/upload/complete/${initData.track_id}`,
        );
        const result = completeRes.data;

        if (!result?.success && result?.existing_track_id) {
          updateFile(item.id, {
            status: 'duplicate',
            isDuplicate: true,
            existingTrackId: result.existing_track_id,
            trackId: result.existing_track_id,
            error: null,
          });
          toast(`Already in library: ${item.file.name}`);
          continue;
        }

        if (!result?.success) throw new Error(result?.message || 'Upload failed');

        const newCredits = result.credits_remaining ?? credits;
        setCredits(newCredits);

        updateFile(item.id, {
          status: 'raw',
          progress: 100,
          trackId: result.track?.id,
          error: null,
        });
        toast.success(`Uploaded: ${item.file.name} (−${result.upload_cost} credits)`);
      } catch (err) {
        const detailRaw = err.response?.data?.detail;
        const msg = (typeof detailRaw === 'string')
          ? detailRaw
          : (detailRaw?.message || err.message || 'Upload failed');
        updateFile(item.id, { status: 'failed', error: msg });

        // Special-case the subscription paywall — nudge the user to Billing.
        if (detailRaw?.code === 'subscription_required') {
          toast.error('Uploads require a Pro subscription — redirecting to Billing…');
          setTimeout(() => { window.location.href = '/billing'; }, 1200);
        } else {
          toast.error(`Failed: ${item.file.name} — ${msg}`);
        }

        // Best-effort cleanup: abort the reserved track so credits refund
        // and no orphan row lingers in the DB / R2.
        if (initData?.track_id) {
          try {
            await axios.post(`${API}/tracks/upload/abort/${initData.track_id}`);
          } catch { /* swallow — server will GC eventually */ }
        }
      }
    }

    // Refresh balance
    axios.get(`${API}/credits/balance`).then(r => setCredits(r.data.credits)).catch(() => {});
    setUploading(false);
  };

  // ─── Queue a single track for analysis ───────────────────────────────────
  const analyzeTrack = async (item) => {
    if (!item.trackId) return;
    try {
      updateFile(item.id, { status: 'queued' });
      await axios.post(`${API}/upload/analyze/${item.trackId}`);
      updateFile(item.id, { status: 'queued' });
      toast.success(`Analysis queued for: ${item.file.name}`);

      // Poll for status every 4 seconds
      const timer = setInterval(async () => {
        try {
          const r = await axios.get(`${API}/upload/status/${item.trackId}`);
          const s = r.data.upload_status;
          updateFile(item.id, { status: s, analysis: r.data.analysis ?? null });
          if (s === 'ready' || s === 'failed') {
            clearInterval(timer);
            delete pollTimers.current[item.id];
            if (s === 'ready') {
              toast.success(`Analysis complete: ${item.file.name}`);
              axios.get(`${API}/credits/balance`).then(r2 => setCredits(r2.data.credits)).catch(() => {});
            } else {
              toast.error(`Analysis failed: ${item.file.name}`);
            }
          } else {
            updateFile(item.id, { status: 'analyzing' });
          }
        } catch { /* silent */ }
      }, 4000);
      pollTimers.current[item.id] = timer;
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to queue analysis';
      updateFile(item.id, { status: 'raw', error: msg });
      toast.error(msg);
    }
  };

  // ─── Analyze all uploaded-but-unanalyzed tracks ───────────────────────────
  const analyzeAll = async () => {
    const rawItems = files.filter(f => f.status === 'raw' && f.trackId);
    const cost = rawItems.length * 2;
    if (credits < cost) { toast.error(`Need ${cost} credits to analyze ${rawItems.length} tracks`); return; }
    for (const item of rawItems) await analyzeTrack(item);
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  const rawCount      = files.filter(f => f.status === 'raw').length;
  const readyCount    = files.filter(f => f.status === 'ready').length;
  const analyzingCount = files.filter(f => ['queued', 'analyzing'].includes(f.status)).length;

  return (
    <div className="p-4 sm:p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A] mb-1">Upload Tracks</h1>
        <p className="font-body text-[#64748B] text-sm">Upload MP3s to your cloud library. Each file is charged based on size; analysis is a separate step.</p>
      </div>

      {/* Subscription paywall — soft overlay for free users */}
      {!isSubscribed && (
        <div
          className="mb-6 rounded-2xl border-2 border-[#2563FF]/30 bg-gradient-to-br from-[#EFF6FF] to-white p-5 flex items-start gap-4 flex-wrap"
          data-testid="upload-paywall-banner"
        >
          <div className="w-10 h-10 rounded-lg bg-[#2563FF] text-white flex items-center justify-center flex-shrink-0">
            <Zap className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <p className="font-heading text-base text-[#0F172A] mb-1">Uploads require Pro Monthly</p>
            <p className="font-body text-xs text-[#64748B]">
              Subscribe for <b>$9.99/mo</b> to upload &amp; analyse tracks (200 credits/month, roll over up to 100 unused). Your existing library and playback stay free forever.
            </p>
          </div>
          <Link
            to="/billing"
            className="px-4 py-2 rounded-lg bg-[#2563FF] text-white text-sm font-heading hover:bg-[#1D4ED8] transition-colors inline-flex items-center gap-1.5 flex-shrink-0"
            data-testid="upload-paywall-cta"
          >
            <Zap className="w-3.5 h-3.5" /> Subscribe now
          </Link>
        </div>
      )}

      {/* Credit bar */}
      <div className="flex items-center justify-between mb-6 px-4 py-3 bg-white border border-[#E2E8F0] rounded-xl">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-[#2563FF]" />
          <span className="font-heading text-sm text-[#0F172A]">Credit balance</span>
        </div>
        <div className="flex items-center gap-4">
          {files.some(f => f.status === 'pending') && (
            <span className="font-mono text-xs text-amber-600">
              −{totalUploadCost} credits for {files.filter(f => f.status === 'pending').length} uploads
            </span>
          )}
          <span
            data-testid="credit-balance-upload"
            className={`font-heading font-bold text-lg ${credits < 10 ? 'text-red-500' : 'text-[#2563FF]'}`}
          >
            {credits} <span className="text-xs font-normal text-[#64748B]">credits</span>
          </span>
        </div>
      </div>

      {/* Drop zone */}
      <motion.div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        animate={{ borderColor: isDragging ? '#2563FF' : '#E2E8F0', scale: isDragging ? 1.01 : 1 }}
        className="relative cursor-pointer mb-6 p-10 border-2 border-dashed rounded-2xl flex flex-col items-center gap-3 bg-white transition-colors select-none"
        data-testid="upload-dropzone"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".mp3,audio/mpeg"
          multiple
          className="hidden"
          onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
        />
        <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${isDragging ? 'bg-[#2563FF]/10' : 'bg-[#F5F7FB]'}`}>
          <Upload className={`w-7 h-7 ${isDragging ? 'text-[#2563FF]' : 'text-[#94A3B8]'}`} />
        </div>
        <p className="font-heading text-[#0F172A] text-base">Drop MP3 files here or <span className="text-[#2563FF]">browse</span></p>
        <p className="font-body text-xs text-[#94A3B8]">1–4 credits per file depending on size · 2 credits per analysis</p>

        {/* Credit tier guide */}
        <div className="mt-3 flex gap-3 flex-wrap justify-center">
          {[['< 5 MB','1 cr'],['5–15 MB','2 cr'],['15–30 MB','3 cr'],['30 MB+','4 cr']].map(([size, cr]) => (
            <span key={size} className="font-mono text-[10px] px-2 py-0.5 rounded-full bg-[#F5F7FB] text-[#64748B] border border-[#E2E8F0]">{size} → {cr}</span>
          ))}
        </div>
      </motion.div>

      {/* File queue */}
      <AnimatePresence>
        {files.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mb-6 space-y-2"
          >
            {/* Bulk action bar */}
            <div className="flex items-center justify-between mb-3">
              <span className="font-heading text-sm text-[#0F172A]">{files.length} file{files.length !== 1 ? 's' : ''}</span>
              <div className="flex gap-2">
                {rawCount > 0 && (
                  <button
                    data-testid="analyze-all-btn"
                    onClick={analyzeAll}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-heading rounded-lg bg-[#F5F7FB] border border-[#E2E8F0] hover:border-[#2563FF]/50 text-[#0F172A] transition-colors"
                  >
                    <BarChart2 className="w-3.5 h-3.5 text-[#2563FF]" />
                    Analyze all ({rawCount} × 2 cr)
                  </button>
                )}
                {files.some(f => f.status === 'pending') && (
                  <button
                    data-testid="upload-all-btn"
                    onClick={uploadAll}
                    disabled={!canUpload}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-heading rounded-lg bg-[#2563FF] text-white disabled:opacity-40 hover:bg-[#1d4ed8] transition-colors"
                  >
                    {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                    Upload all ({totalUploadCost} cr)
                  </button>
                )}
              </div>
            </div>

            {files.map((item) => {
              const meta = STATUS_META[item.status] || STATUS_META.pending;
              const Icon = meta.icon;
              const cost = uploadCreditCost(item.file.size);
              const isSpinning = ['uploading', 'analyzing'].includes(item.status);

              return (
                <motion.div
                  key={item.id}
                  layout
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  className={`flex items-center gap-3 px-4 py-3 bg-white border rounded-xl group transition-colors ${
                    item.isDuplicate
                      ? 'border-violet-200 bg-violet-50/50'
                      : 'border-[#E2E8F0]'
                  }`}
                  data-testid={`upload-file-row-${item.id}`}
                >
                  <Music className={`w-5 h-5 flex-shrink-0 ${item.isDuplicate ? 'text-violet-400' : 'text-[#94A3B8]'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-heading text-sm text-[#0F172A] truncate">{item.file.name}</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="font-mono text-[10px] text-[#94A3B8]">{fmtSize(item.file.size)}</span>
                      {item.status === 'pending' && (
                        <span className="font-mono text-[10px] text-amber-600">{cost} cr upload</span>
                      )}
                      {item.isDuplicate && item.existingTrackId && (
                        <Link
                          to={`/track/${item.existingTrackId}`}
                          className="font-mono text-[10px] text-violet-600 hover:underline flex items-center gap-0.5"
                          data-testid={`duplicate-link-${item.id}`}
                          onClick={e => e.stopPropagation()}
                        >
                          View in library →
                        </Link>
                      )}
                      {item.error && <span className="font-mono text-[10px] text-red-500 truncate">{item.error}</span>}
                      {item.analysis && (
                        <span className="font-mono text-[10px] text-[#64748B]">
                          {item.analysis.bpm?.toFixed(0)} BPM · {item.analysis.camelot} · {item.analysis.energy?.toFixed(0)}%
                        </span>
                      )}
                    </div>
                    {item.status === 'uploading' && (
                      <div className="mt-1.5 h-1 bg-[#F5F7FB] rounded-full overflow-hidden">
                        <motion.div
                          className="h-full bg-gradient-to-r from-[#2563FF] to-[#14E6C9]"
                          animate={{ width: `${item.progress}%` }}
                          transition={{ duration: 0.2 }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Status badge */}
                  <div className={`flex items-center gap-1 flex-shrink-0 ${meta.color}`}>
                    <Icon className={`w-4 h-4 ${isSpinning ? 'animate-spin' : ''}`} />
                    <span className="font-mono text-[10px] hidden sm:inline">{meta.label}</span>
                  </div>

                  {/* Analyze button for uploaded-but-unanalyzed tracks */}
                  {item.status === 'raw' && item.trackId && (
                    <button
                      data-testid={`analyze-btn-${item.id}`}
                      onClick={() => analyzeTrack(item)}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-heading bg-[#F5F7FB] border border-[#E2E8F0] hover:border-[#2563FF]/50 text-[#0F172A] transition-colors flex-shrink-0"
                    >
                      <BarChart2 className="w-3 h-3 text-[#2563FF]" /> Analyze (2 cr)
                    </button>
                  )}

                  {/* Retry for failed */}
                  {item.status === 'failed' && (
                    <button
                      onClick={() => updateFile(item.id, { status: 'pending', error: null })}
                      className="p-1.5 rounded-lg hover:bg-[#F5F7FB] transition-colors flex-shrink-0"
                    >
                      <RefreshCw className="w-3.5 h-3.5 text-[#64748B]" />
                    </button>
                  )}

                  {/* Upload anyway for duplicates */}
                  {item.status === 'duplicate' && (
                    <button
                      data-testid={`upload-anyway-${item.id}`}
                      onClick={() => updateFile(item.id, { status: 'pending', isDuplicate: false, error: null })}
                      className="flex-shrink-0 font-mono text-[10px] text-[#64748B] hover:text-[#2563FF] underline underline-offset-2 transition-colors"
                    >
                      Upload anyway
                    </button>
                  )}

                  {/* Remove */}
                  {!['uploading', 'analyzing', 'queued'].includes(item.status) && (
                    <button
                      data-testid={`remove-file-btn-${item.id}`}
                      onClick={() => removeFile(item.id)}
                      className="p-1.5 rounded-lg hover:bg-[#F5F7FB] transition-colors flex-shrink-0 opacity-0 group-hover:opacity-100"
                    >
                      <X className="w-3.5 h-3.5 text-[#94A3B8]" />
                    </button>
                  )}
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Summary stats */}
      {(readyCount > 0 || analyzingCount > 0) && (
        <div className="grid grid-cols-3 gap-3 mt-2">
          {[
            { label: 'Ready',     value: readyCount,    color: 'text-emerald-600' },
            { label: 'Analyzing', value: analyzingCount, color: 'text-blue-500'   },
            { label: 'Stored',    value: rawCount,       color: 'text-[#64748B]'  },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-white border border-[#E2E8F0] rounded-xl px-4 py-3 text-center">
              <p className={`font-heading text-xl font-bold ${color}`}>{value}</p>
              <p className="font-body text-xs text-[#94A3B8] mt-0.5">{label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Low-credit warning */}
      {credits < 5 && (
        <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
          <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-heading text-sm text-amber-800">Running low on credits</p>
            <p className="font-body text-xs text-amber-700 mt-0.5">You have {credits} credit{credits !== 1 ? 's' : ''} remaining. Top up from your profile page.</p>
          </div>
        </div>
      )}
    </div>
  );
}
